// Pop-up notifications API. Campaigns and automation templates are the same resource with a
// different `kind`; everything else here answers a question the composer asks while you type.
import { API_URL } from '../config/config';
import { getRequest, postRequest, patchRequest, deleteRequest } from '../utils/axiosUtils';
import qs from '../utils/queryString';

const URL = `${API_URL}/admin/popup`;

const data = (res) => {
  if (!res) return null;
  if (res.status >= 200 && res.status < 300) return res.data?.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

/** The four tiles: live campaigns, impressions, unique viewers, share of members reached. */
export const fetchPopupStats = async (days = 7) =>
  data(await getRequest({ url: `${URL}/stats?days=${days}` }));

// Campaigns and templates are two different lists behind one path. Without distinct abort keys
// they are loaded in parallel and cancel each other — which read as "No campaigns yet", forever.
export const fetchPopups = async (kind) =>
  data(await getRequest({ url: `${URL}?kind=${kind}`, abortKey: `${URL}:${kind}` }));

export const createPopup = async (payload) => data(await postRequest({ url: URL, data: payload }));

/** End a campaign early, or turn a template on/off. */
export const setPopupActive = async (popupId, active) =>
  data(await patchRequest({ url: `${URL}/${popupId}`, data: { active } }));

/** Edit what a pop-up says and how it behaves. Only the keys sent are touched — never the
 *  audience, which was snapshotted at launch and is what the reach percentage is measured against. */
export const updatePopup = async (popupId, patch) =>
  data(await patchRequest({ url: `${URL}/${popupId}`, data: patch }));

/** Permanent, and it takes the impressions with it. Ending a campaign is the reversible move. */
export const deletePopup = async (popupId) =>
  data(await deleteRequest({ url: `${URL}/${popupId}` }));

/** How many members the current audience covers. */
export const fetchReach = async (audience) => {
  const res = await getRequest({
    url: `${URL}/reach?${qs({
      mode: audience.mode,
      gender: audience.gender,
      country: audience.country,
      city: audience.city,
      userIds: audience.userIds?.join(','),
    })}`,
  });
  return data(res)?.count ?? null;
};

/** Countries members actually live in; with a country, that country's cities. */
export const fetchGeo = async (country) =>
  data(await getRequest({ url: `${URL}/geo?${qs({ country })}` }));

export const searchMembers = async (search) =>
  data(await getRequest({ url: `${URL}/members?${qs({ search })}` }));
